import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal } from "lucide-react";
import api from "@/services/api";
import { useQuery } from "@tanstack/react-query";

interface SocieteNote {
  societe_id: string;
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  poids_qualite_offre: number;
  poids_prix: number;
  poids_durabilite: number;
  poids_references: number;
  score_pondere: number;
}

interface GlobalRankingViewProps {
  aoId: string;
}

export const GlobalRankingView: React.FC<GlobalRankingViewProps> = ({ aoId }) => {
  // Fetch societes with notes for this AO
  const { data: societeNotes, isLoading } = useQuery({
    queryKey: ['ao-societe-notes-global', aoId],
    queryFn: async () => {
      // Fetch notes via API
      const { data: notesResponse } = await api.get(`/appels-offres/${aoId}/societe-notes`);
      const notesData = notesResponse?.data || [];

      // Fetch societes that have depots for this AO via API
      const { data: societesResponse } = await api.get(`/appels-offres/${aoId}/societes`);
      const societesData = societesResponse?.data || [];

      // Get unique societes
      const societesMap = new Map<string, { id: string; nom: string }>();
      societesData.forEach((societe: any) => {
        if (!societesMap.has(societe.id)) {
          societesMap.set(societe.id, { id: societe.id, nom: societe.nom });
        }
      });

      // Merge notes with societes
      const notesMap = new Map(notesData.map((n: any) => [n.societeId, {
        note_qualite_offre: n.noteQualiteOffre,
        note_prix: n.notePrix,
        note_durabilite: n.noteDurabilite,
        note_references: n.noteReferences,
        poids_qualite_offre: n.poidsQualiteOffre,
        poids_prix: n.poidsPrix,
        poids_durabilite: n.poidsDurabilite,
        poids_references: n.poidsReferences
      }]));

      const result: SocieteNote[] = [];
      societesMap.forEach(societe => {
        const note = notesMap.get(societe.id);
        const poids_qualite_offre = note?.poids_qualite_offre ?? 25;
        const poids_prix = note?.poids_prix ?? 25;
        const poids_durabilite = note?.poids_durabilite ?? 25;
        const poids_references = note?.poids_references ?? 25;

        const note_qualite_offre = note?.note_qualite_offre ?? null;
        const note_prix = note?.note_prix ?? null;
        const note_durabilite = note?.note_durabilite ?? null;
        const note_references = note?.note_references ?? null;

        // Calculate weighted score
        let totalWeight = 0;
        let weightedSum = 0;

        if (note_qualite_offre !== null) {
          weightedSum += note_qualite_offre * poids_qualite_offre;
          totalWeight += poids_qualite_offre;
        }
        if (note_prix !== null) {
          weightedSum += note_prix * poids_prix;
          totalWeight += poids_prix;
        }
        if (note_durabilite !== null) {
          weightedSum += note_durabilite * poids_durabilite;
          totalWeight += poids_durabilite;
        }
        if (note_references !== null) {
          weightedSum += note_references * poids_references;
          totalWeight += poids_references;
        }

        const score_pondere = totalWeight > 0 ? weightedSum / totalWeight : 0;

        result.push({
          societe_id: societe.id,
          societe_nom: societe.nom,
          note_qualite_offre,
          note_prix,
          note_durabilite,
          note_references,
          poids_qualite_offre,
          poids_prix,
          poids_durabilite,
          poids_references,
          score_pondere
        });
      });

      return result;
    },
    enabled: !!aoId
  });

  // Sort by weighted score (descending) and compute rankings per criteria
  const rankedData = useMemo(() => {
    if (!societeNotes || societeNotes.length === 0) return [];

    // Sort by each criteria to get individual rankings
    const sortedByQualite = [...societeNotes]
      .filter(s => s.note_qualite_offre !== null)
      .sort((a, b) => (b.note_qualite_offre ?? 0) - (a.note_qualite_offre ?? 0));
    
    const sortedByPrix = [...societeNotes]
      .filter(s => s.note_prix !== null)
      .sort((a, b) => (b.note_prix ?? 0) - (a.note_prix ?? 0));
    
    const sortedByDurabilite = [...societeNotes]
      .filter(s => s.note_durabilite !== null)
      .sort((a, b) => (b.note_durabilite ?? 0) - (a.note_durabilite ?? 0));
    
    const sortedByReferences = [...societeNotes]
      .filter(s => s.note_references !== null)
      .sort((a, b) => (b.note_references ?? 0) - (a.note_references ?? 0));

    // Create ranking maps
    const qualiteRanks = new Map(sortedByQualite.map((s, i) => [s.societe_id, i + 1]));
    const prixRanks = new Map(sortedByPrix.map((s, i) => [s.societe_id, i + 1]));
    const durabiliteRanks = new Map(sortedByDurabilite.map((s, i) => [s.societe_id, i + 1]));
    const referencesRanks = new Map(sortedByReferences.map((s, i) => [s.societe_id, i + 1]));

    // Sort by weighted score (best to worst)
    const sortedByScore = [...societeNotes].sort((a, b) => b.score_pondere - a.score_pondere);

    return sortedByScore.map((societe, index) => ({
      ...societe,
      rang_global: index + 1,
      rang_qualite: qualiteRanks.get(societe.societe_id) ?? null,
      rang_prix: prixRanks.get(societe.societe_id) ?? null,
      rang_durabilite: durabiliteRanks.get(societe.societe_id) ?? null,
      rang_references: referencesRanks.get(societe.societe_id) ?? null
    }));
  }, [societeNotes]);

  const getRankBadge = (rank: number | null, total: number) => {
    if (rank === null) return <span className="text-muted-foreground">-</span>;
    
    if (rank === 1) {
      return (
        <Badge className="bg-yellow-500 hover:bg-yellow-600 gap-1">
          <Trophy className="h-3 w-3" />
          1er
        </Badge>
      );
    }
    if (rank === 2) {
      return (
        <Badge className="bg-gray-400 hover:bg-gray-500 gap-1">
          <Medal className="h-3 w-3" />
          2ème
        </Badge>
      );
    }
    if (rank === 3) {
      return (
        <Badge className="bg-amber-700 hover:bg-amber-800 gap-1">
          <Medal className="h-3 w-3" />
          3ème
        </Badge>
      );
    }
    return <Badge variant="outline">{rank}ème</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!rankedData || rankedData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Aucune société avec des notes pour cet appel d'offres.
        </CardContent>
      </Card>
    );
  }

  const totalSocietes = rankedData.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Classement Global ({totalSocietes} sociétés)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Rang</TableHead>
              <TableHead>Société</TableHead>
              <TableHead className="text-center">Score Final</TableHead>
              <TableHead className="text-center">Qualité de l'offre</TableHead>
              <TableHead className="text-center">Prix</TableHead>
              <TableHead className="text-center">Durabilité</TableHead>
              <TableHead className="text-center">Références</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rankedData.map((societe) => (
              <TableRow key={societe.societe_id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {societe.rang_global === 1 && <Trophy className="h-5 w-5 text-yellow-500" />}
                    {societe.rang_global === 2 && <Medal className="h-5 w-5 text-gray-400" />}
                    {societe.rang_global === 3 && <Medal className="h-5 w-5 text-amber-700" />}
                    <span className="font-bold text-lg">#{societe.rang_global}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{societe.societe_nom}</div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="font-bold text-lg">{societe.score_pondere.toFixed(2)}/5</div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm">
                      {societe.note_qualite_offre !== null ? `${societe.note_qualite_offre}/5` : '-'}
                    </span>
                    {getRankBadge(societe.rang_qualite, totalSocietes)}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm">
                      {societe.note_prix !== null ? `${societe.note_prix}/5` : '-'}
                    </span>
                    {getRankBadge(societe.rang_prix, totalSocietes)}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm">
                      {societe.note_durabilite !== null ? `${societe.note_durabilite}/5` : '-'}
                    </span>
                    {getRankBadge(societe.rang_durabilite, totalSocietes)}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm">
                      {societe.note_references !== null ? `${societe.note_references}/5` : '-'}
                    </span>
                    {getRankBadge(societe.rang_references, totalSocietes)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
